import { cloneElement, isValidElement, useEffect, useRef, type ReactElement, type ReactNode } from 'react'
import { useConnectAdapter } from '@/hooks'

interface ConnectFlowProps {
  adapterId: string
  onComplete: (result: { adapterId: string; ok: boolean; error?: string }) => void
  children: ReactNode
}

export function ConnectFlow({ adapterId, onComplete, children }: ConnectFlowProps) {
  const { mutate: connect } = useConnectAdapter()
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const listenerRef = useRef<((e: MessageEvent) => void) | null>(null)

  function cleanup() {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (listenerRef.current !== null) {
      window.removeEventListener('message', listenerRef.current)
      listenerRef.current = null
    }
  }

  useEffect(() => () => cleanup(), [])

  function handleClick() {
    connect(adapterId, {
      onSuccess({ authorization_url }) {
        const popup = window.open(
          authorization_url,
          'oauth-popup',
          'width=600,height=700,left=200,top=100,resizable=yes,scrollbars=yes',
        )

        function messageHandler(event: MessageEvent) {
          if (event.origin !== window.location.origin) return
          if (event.data?.type !== 'adapter-oauth-callback') return
          cleanup()
          onComplete({
            adapterId,
            ok: event.data.ok === true,
            error: event.data.error as string | undefined,
          })
        }

        listenerRef.current = messageHandler
        window.addEventListener('message', messageHandler)

        pollRef.current = setInterval(() => {
          if (popup?.closed) {
            cleanup()
            onComplete({ adapterId, ok: false, error: 'cancelled' })
          }
        }, 250)
      },
      onError(err) {
        onComplete({
          adapterId,
          ok: false,
          error: err instanceof Error ? err.message : 'Failed to initiate connect',
        })
      },
    })
  }

  if (isValidElement(children)) {
    return cloneElement(children as ReactElement<{ onClick?: React.MouseEventHandler }>, {
      onClick: handleClick,
    })
  }
  return (
    <span role="button" tabIndex={0} onClick={handleClick} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }} className="contents">
      {children}
    </span>
  )
}
